import * as React from 'react';
import styled from '@emotion/styled';
import {
  BORDER_RADIUS,
  COLOR,
  FONT_SIZE,
  FONT_WEIGHT,
  BREAKPOINTS,
} from '@ordermentum/ui-constants';
import Icon from '../Icon';
import Button from '../Button';
import { ButtonType } from '../Button/Button.types';

export type BannerType =
  | 'error'
  | 'info'
  | 'warning'
  | 'plain'
  | 'grey'
  | 'success'
  | 'alert';

export type WrapperProps = { type: BannerType };

const COLOR_MAP = {
  info: COLOR.BLUE_200,
  error: COLOR.RED_200,
  warning: COLOR.YELLOW_200,
  plain: COLOR.WHITE,
  success: COLOR.GREEN_MINT,
  grey: COLOR.GRAY_F0F0F0,
  alert: COLOR.BRAND_FFF4EE,
};

const BORDER_MAP = {
  info: COLOR.BLUE_500,
  error: COLOR.RED_500,
  warning: COLOR.YELLOW_500,
  plain: COLOR.GRAY_CACACA,
  success: COLOR.GREEN_CLOVER,
  grey: COLOR.GRAY_F0F0F0,
  alert: COLOR.BRAND_FFF4EE,
};

const Wrapper = styled('div')`
  background: ${(props: WrapperProps) => COLOR_MAP[props.type]};
  border: 1px solid ${(props: WrapperProps) => BORDER_MAP[props.type]};
  border-radius: ${BORDER_RADIUS};
  font-size: ${FONT_SIZE.SM};
  margin: 0 0 1rem;
  min-height: 49px; /* min height of 1 row of text */
  padding: 0rem 1rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;

  @media (max-width: ${BREAKPOINTS.MOBILE_MAX}) {
    flex-flow: row wrap;
  }
`;

const PrimaryContent = styled('div')`
  display: flex;
  flex-direction: row;
  flex: 2 1 auto;
  align-items: center;
`;

const IconWrapper = styled('div')`
  flex: 0 0 auto;
  margin-right: 1rem;
`;

const DismissWrapper = styled('div')`
  flex: 0 0 auto;
  margin-left: 1rem;
  margin-right: 1rem;
  cursor: pointer;
`;

const ActionWrapper = styled('div')`
  display: flex;
  flex: 0 0 auto;
  margin: 0.5rem 0 0.5rem 1rem;
  cursor: pointer;

  @media (max-width: ${BREAKPOINTS.MOBILE_MAX}) {
    margin: 0 0 0.5rem 0;
    width: 100%;
  }
`;

const Body = styled('div')`
  flex: 1;
  font-weight: ${FONT_WEIGHT.NORMAL};

  ul {
    margin: 1rem 0;
    padding: 0;
  }

  li {
    font-weight: ${FONT_WEIGHT.NORMAL}; /* override global styles */
    list-style: none;
    margin: 0.5rem 0;

    .error-text {
      font-size: ${FONT_SIZE.SM};
      font-weight: ${FONT_WEIGHT.NORMAL};
    }
  }
`;

type Props = {
  type?: BannerType;
  children: any;
  noIcon?: boolean;
  icon?: string;
  dismiss?: React.ReactNode | boolean;
  onDismiss?: () => void;
  actionText?: string;
  onAction?: () => void;
};

const IconColor = {
  info: 'blue_window',
  warning: 'warning',
  error: 'red',
  plain: 'orange',
  grey: 'default',
  alert: 'black',
};

type DismissIconProps = {
  dismiss?: React.ReactNode;
  onDismiss?: () => void;
};

const DismissIcon = ({ dismiss, onDismiss }: DismissIconProps) => {
  if (!dismiss) {
    return <React.Fragment />;
  }

  let icon: React.ReactNode;
  if (typeof dismiss === 'boolean' && dismiss) {
    icon = <Icon name="cancel" color={COLOR.GRAY} size="x3s" />;
  } else {
    icon = dismiss;
  }

  return (
    <DismissWrapper
      onClick={onDismiss}
      onKeyPress={onDismiss}
      role="button"
      tabIndex={0}
    >
      {icon}
    </DismissWrapper>
  );
};

type ActionButtonProps = {
  type?: ButtonType;
  onClick?: () => void;
  actionText?: string;
};

const ActionButton = ({
  type = 'primary',
  onClick,
  actionText,
}: ActionButtonProps) => {
  if (!onClick) return null;

  return (
    <ActionWrapper>
      <Button size="max" borderRadius="4" type={type} onClick={onClick}>
        {actionText}
      </Button>
    </ActionWrapper>
  );
};

const Banner = (props: Props) => {
  const { type = 'error', icon = 'error', children } = props;
  return (
    <Wrapper data-testid="banner-wrapper" type={type}>
      <PrimaryContent>
        {!props.noIcon && (
          <IconWrapper>
            <Icon name={icon} color={IconColor[type] ?? 'red'} />
          </IconWrapper>
        )}
        <Body>{children}</Body>
        <DismissIcon dismiss={props.dismiss} onDismiss={props.onDismiss} />
      </PrimaryContent>
      <ActionButton onClick={props.onAction} actionText={props.actionText} />
    </Wrapper>
  );
};

export default Banner;
